import type { SimulatorFontSchemaType, SimulatorSchemaType } from '@vedalib/editor/lib/brand'
import type { SimulatorArea, SimulatorElementValue } from '@vedalib/editor/lib/elements'
import { CONFIG_ELEMENTS } from '@vedalib/editor/lib/elements'
import { createContext, useContextSelector } from 'use-context-selector'

import { NOOP } from 'constants/commonConstans'
import type { ElementFontCss, ElementStyleCss } from 'services/Branding/types'
import { PREVIEW_MODE } from 'services/Store/Project/constants'
import type { IBlockMode } from 'services/Store/Project/types'

import type { SimulatorState } from './simulatorTypes'
import type { ResizeDirection } from './useSelectAreaActions'

export interface ILabeledGraphicProps {
  styles: ElementStyleCss<SimulatorSchemaType>
  font: ElementFontCss<SimulatorFontSchemaType>
  mode: IBlockMode
  value: SimulatorElementValue
  waiting?: boolean
  setState: ((value: SimulatorState) => void) | undefined
  showHighlight: boolean
  onLinkArea: (itemId: string) => void
  onAddNewItem: () => void
  onAddField: () => void
  onDeleteArea: (areaId: string) => void
  onDeleteField: (fieldId: string) => void
  onResize: (
    e: React.MouseEvent<HTMLDivElement>,
    area: SimulatorArea,
    direction: ResizeDirection,
  ) => void
  onChange?: (value: SimulatorElementValue) => void
  elementId?: string
  state?: SimulatorState
  validateFields: () => boolean
  onFieldValueChange: (fieldId: string, value: string) => void
  validateFieldOnBlur: (fieldId: string) => void
  invalidFields: { id: string; message: string }[]
}

const SimulatorContext = createContext<ILabeledGraphicProps>({
  styles: {} as ElementStyleCss<SimulatorSchemaType>,
  font: {} as ElementFontCss<SimulatorFontSchemaType>,
  mode: PREVIEW_MODE,
  waiting: false,
  value: CONFIG_ELEMENTS.simulator.defaultValue,
  setState: NOOP,
  showHighlight: false,
  onLinkArea: NOOP,
  onAddNewItem: NOOP,
  onAddField: NOOP,
  onDeleteArea: NOOP,
  onDeleteField: NOOP,
  onResize: NOOP,
  onChange: NOOP,
  elementId: '',
  state: {
    active: null,
    incomplete: false,
  },
  validateFields: () => true,
  onFieldValueChange: NOOP,
  validateFieldOnBlur: NOOP,
  invalidFields: [],
})

export const useSimulatorContext = <R>(selector: (c: ILabeledGraphicProps) => R) =>
  useContextSelector(SimulatorContext, selector)

export default SimulatorContext
